/**
 * The extra information collected right after sign-up.
 *
 * ## Why here and not in Clerk's sign-up form
 *
 * Clerk's prebuilt `<SignUp />` cannot be extended with arbitrary fields — only the ones Clerk
 * knows about (name, username, phone), which are Dashboard toggles. The alternative to this file is
 * a fully custom sign-up flow via `useSignUp()`, which means rebuilding email verification, error
 * states, password policy and the Dutch copy that `@clerk/localizations` gives us for free. Asking
 * these profile questions *after* sign-up costs a screen and keeps all of that.
 *
 * ## Where it is stored
 *
 * `user.unsafeMetadata` — the one metadata bucket a frontend may write. "Unsafe" means
 * *client-writable*, so treat it as a viewer preference, never as an authorization input: anything
 * the backend must trust belongs in `publicMetadata`, written server-side.
 *
 * ## Adding a field
 *
 * Extend {@link ViewerProfile}, then add its control and validation to the profile step.
 */

export const CHANNEL_OPTIONS = [
  { key: "tvoost", name: "TV Oost" },
  { key: "rtv", name: "RTV" },
  { key: "ringtv", name: "Ring TV" },
] as const;

export type PreferredChannelKey = (typeof CHANNEL_OPTIONS)[number]["key"];

export type ViewerProfile = {
  /** Belgian postcode. Kept separately from the viewer's station choice. */
  postcode?: string;
  /** Station explicitly selected by the viewer; never inferred from the postcode. */
  preferredChannel?: PreferredChannelKey;
  /** When both required profile fields were last saved. */
  completedAt?: string;
};

export const PROFILE_QUESTION = {
  label: "Postcode",
  hint: "Vul je postcode in en kies zelf je voorkeurszender.",
  placeholder: "bv. 9000",
  inputMode: "numeric" as const,
  maxLength: 4,
  /** Belgian postcodes run 1000–9999, so exactly four digits not starting at zero. */
  isValid: (value: string) => /^[1-9]\d{3}$/.test(value.trim()),
  invalidMessage: "Vul een geldige postcode in (4 cijfers).",
};

export function isPreferredChannelKey(
  value: unknown,
): value is PreferredChannelKey {
  return (
    typeof value === "string" &&
    CHANNEL_OPTIONS.some((channel) => channel.key === value)
  );
}

/** Reads the profile back out of Clerk's untyped metadata bag. */
export function readProfile(metadata: unknown): ViewerProfile {
  const bag = (metadata ?? {}) as Record<string, unknown>;
  return {
    postcode: typeof bag.postcode === "string" ? bag.postcode : undefined,
    preferredChannel: isPreferredChannelKey(bag.preferredChannel)
      ? bag.preferredChannel
      : undefined,
    completedAt:
      typeof bag.profileCompletedAt === "string"
        ? bag.profileCompletedAt
        : undefined,
  };
}

/** Existing accounts missing either required field are sent through the profile step once. */
export function needsAsking(metadata: unknown): boolean {
  const profile = readProfile(metadata);
  return (
    !PROFILE_QUESTION.isValid(profile.postcode ?? "") ||
    !profile.preferredChannel
  );
}
